import React, { useState, useEffect, useMemo } from "react";
import { Box, useTheme, FormControl, RadioGroup, FormControlLabel, Radio, Grid, Stack, Button, Checkbox } from "@mui/material";
import { FormattedMessage } from "react-intl";
import messages from "./components/messages";
import { tokens } from "../../assets/theme";
import { useQuery } from "@tanstack/react-query";
import AxionControls from "../../components/controls/AxionControls";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import dayjs from "dayjs";
import { getTimespan } from "../../fetchers/timespan";
import { fetchMachineStopsByDate, fetchMachineList } from "../../fetchers/machine";
import { getStopReasonCodes } from "../stopReasonCodes/hooks/fetchers";
import GridDateTimePicker from "../home/components/GridDateTimePicker";
import MachineStopGrid from "../machineDetails/components/machineStopsGrid";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

function isDateValid(dateStr) {
    return !isNaN(new Date(dateStr));
}

const StopsIndex = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [timeSpan, setTimeSpan] = useLocalStorage("timespan", "currentshift");
    const useDateInterval = timeSpan === 'manual';
    const [timeSpanStartDate, setTimeSpanStartDate] = React.useState(null);
    const [timeSpanEndDate, setTimeSpanEndDate] = React.useState(null);
    const [startDate, setStartDate] = useLocalStorage("startDate", dayjs());
    const [endDate, setEndDate] = useLocalStorage("endDate", dayjs());
    const [selectedValue, setSelectedValue] = React.useState(timeSpan);
    const [showDatepicker, setShowDatePicker] = useState(timeSpan === 'manual');
    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);
    const [selectedMachines, setSelectedMachines] = useLocalStorage("stopsSelectedMachines", []);
    const [showMachineFilter, setShowMachineFilter] = useState(false);
    const [showStopFilter, setShowStopFilter] = useState(false);
    const [showAllMachines, setShowAllMachines] = useState(false);
    const [showOnlyUndefinedStops, setShowOnlyUndefinedStops] = useState(false);

    const maxStopRows = 10500;
    let filteredData = {
        isLoading: false,
        isError: false,
        isFetched: false,
        isSuccess: false,
        data: []
    };

    const DatesForTimespanQuery = useQuery(
        ["datesForTimespan", timeSpan, companyKey],
        () => getTimespan(timeSpan, companyKey),
        {
            staleTime: 120000,
            refetchInterval: 120000,
            keepPreviousData: true
        }
    );

    const machinesQuery = useQuery({
        queryKey: ['machinelist', companyKey],
        queryFn: () => fetchMachineList(companyKey),
        staleTime: 60000,
        refetchInterval: 60000,
        keepPreviousData: true
    });

    useEffect(() => {
        if (selectedMachines && selectedMachines.length === 0) {
            setShowMachineFilter(true);
        }
    }, []);

    useEffect(() => {
        if (DatesForTimespanQuery.isFetched && !DatesForTimespanQuery.isError && DatesForTimespanQuery.data !== undefined && timeSpan !== "manual") {
            setTimeSpanStartDate(DatesForTimespanQuery.data[0].fromDate);
            setTimeSpanEndDate(DatesForTimespanQuery.data[0].toDate);
        }
    }, [DatesForTimespanQuery]);

    useEffect(() => {
        if (selectedValue === "week") {
            setSelectedValue("currentshift");
            setTimeSpan("currentshift");
        }
    }, [selectedValue, timeSpan, timeSpanStartDate]);

    useEffect(() => {
        // If all machines are selected, set the "all" option as selected
        if (selectedMachines.length === machinesQuery.data?.length) {
            setShowAllMachines(true);
        } 
        else {
            setShowAllMachines(false);
        }
    }, [selectedMachines, machinesQuery.data]);

    const machinestopsQuery = useQuery(
        ["machinestopsByDate", companyKey, startDate, endDate, useDateInterval, timeSpanStartDate, timeSpanEndDate],
        () => fetchMachineStopsByDate(companyKey, startDate, endDate, useDateInterval, timeSpanStartDate, timeSpanEndDate, maxStopRows),
        {
            refetchInterval: 60000,
            enabled: (timeSpanEndDate !== null && timeSpanStartDate !== null) || useDateInterval,
            keepPreviousData: true
        }
    );

    const stopReasonCodesQuery = useQuery(
        ["stopReasonCodes", companyKey],
        () => getStopReasonCodes(companyKey),
        {
            keepPreviousData: true
        }
    );

    const machinesOptions = useMemo(() => {
            const arr = machinesQuery.data ?? [];
            return arr.toSorted
                ? arr.toSorted((a, b) => a.machineName.localeCompare(b.machineName))
                : [...arr].sort((a, b) => a.machineName.localeCompare(b.machineName));
        }, [machinesQuery.data]);

    const handleStopRefresh = () => {
        machinestopsQuery.refetch();
    }

    const handleMachineChange = (machine) => {
        setSelectedMachines((prev) => prev.includes(machine) ? prev.filter((item) => item !== machine) : [...prev, machine]);
    };

    const handleShowHideAllMachineChange = (showAllMachines) => {
        if(!showAllMachines) {
            setSelectedMachines([]);
        } 
        else {
            setSelectedMachines(machinesQuery.data.map((item) => item.machineName));
        }

        setShowAllMachines(showAllMachines);
    }

    const handleShowOnlyUndefinedStopsChange = (showOnlyUndefinedStops) => {
        setShowOnlyUndefinedStops(!showOnlyUndefinedStops);
    }

    //Change handle for radio buttons when selecting new timespan for api fetch.
    const handleTimeSpanChange = (event) => {
        setSelectedValue(event.target.value);
        setTimeSpan(event.target.value);
        //Check if manual timespan selected, if selected then show datepickers.
        if (event.target.value !== "manual") {
            setShowDatePicker(false);
            
        } else {
            setShowDatePicker(true);
        }
    };

    const handleStartDateChange = (event) => {
        if (isDateValid(event)) {
            let startDate = new Date(event);
            setStartDate(startDate.toISOString());
        }
    };

    const handleEndDateChange = (event) => {
        if (isDateValid(event)) {
            let endDate = new Date(event);
            setEndDate(endDate.toISOString());
        }
    };

    const controlProps = (item) => ({
        checked: selectedValue === item,
        onChange: handleTimeSpanChange,
        value: item,
        name: "size-radio-button-demo",
        inputProps: { "aria-label": item },
    });

    if (stopReasonCodesQuery.isLoading
        || machinestopsQuery.isLoading
        || machinesQuery.isLoading
        || DatesForTimespanQuery.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    }

    filteredData = {
        isLoading: machinestopsQuery?.isLoading,
        isError: machinestopsQuery?.isError,
        isFetched: machinestopsQuery?.isFetched,
        isSuccess: machinestopsQuery?.isSuccess,
        data: machinestopsQuery.data?.filter((item) => selectedMachines.includes(item.machineName) && (!showOnlyUndefinedStops || item.isUndefined === true))
    };

    let radiobuttons = (
        <FormControl>
            <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
            >
                <FormControlLabel
                    value="currentShift"
                    control={
                        <Radio
                            {...controlProps("currentshift")}
                            sx={{
                                color: "secondary",
                                "&.Mui-checked": {
                                    color: colors.dashbordGreen[500],
                                },
                            }}
                        />
                    }
                    color="success"
                    label={<FormattedMessage {...messages.currentShiftFilter} />}
                />
                <FormControlLabel
                    value="prevShift"
                    control={
                        <Radio
                            {...controlProps("prevShift")}
                            sx={{
                                color: "secondary",
                                "&.Mui-checked": {
                                    color: colors.dashbordGreen[500],
                                },
                            }}
                        />
                    }
                    label={<FormattedMessage {...messages.prevShiftFilter} />}
                />
                <FormControlLabel
                    value="24h"
                    control={
                        <Radio
                            {...controlProps("24h")}
                            sx={{
                                color: "secondary",
                                "&.Mui-checked": {
                                    color: colors.dashbordGreen[500],
                                },
                            }}
                        />
                    }
                    label={<FormattedMessage {...messages.dayFilter} />}
                />
                <FormControlLabel
                    value="72h"
                    control={
                        <Radio
                            {...controlProps("72h")}
                            sx={{
                                color: "secondary",
                                "&.Mui-checked": {
                                    color: colors.dashbordGreen[500],
                                },
                            }}
                        />
                    }
                    label={<FormattedMessage {...messages.threeDayFilter} />}
                />

                <FormControlLabel
                    value="manual"
                    control={<Radio {...controlProps("manual")} sx={{
                        color: "secondary",
                        '&.Mui-checked': {
                            color: colors.dashbordGreen[500],
                        },
                    }} />}
                    label={<FormattedMessage {...messages.manualFilter} />}
                />
            </RadioGroup>
        </FormControl>
    );

    const datepicker = (
        <Grid item xs={12} lg={6} sx={{ display: "flex", paddingRight: "10px" }}>
            <Stack direction="row" width="100%" spacing={2}>
                {showDatepicker && (<><GridDateTimePicker
                    label={<FormattedMessage {...messages.fromDate} />}
                    labelTime={<FormattedMessage {...messages.fromTime} />}
                    locale="sv"
                    maxDate={Date().toLocaleString()}
                    minDate=""
                    onChange={handleStartDateChange}
                    date={startDate}
                />
                    <GridDateTimePicker
                        label={<FormattedMessage {...messages.toDate} />}
                        labelTime={<FormattedMessage {...messages.toTime} />}
                        locale="sv"
                        maxDate={Date().toLocaleString()}
                        minDate=""
                        onChange={handleEndDateChange}
                        date={endDate}
                    /> </>)}

            </Stack>
        </Grid>
    );

    return (
        <Box sx={{ width: '100%' }}>
            <Grid container justifyContent="space-between" alignItems="center" sx={{ padding: "5px 20px 0 20px" }}>
                <Grid item xs={12} lg={6}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <FilterAltIcon />
                        <Button
                            sx={{ color: colors.primary[200], ml: 1 }}
                            onClick={() => setShowMachineFilter((prev) => !prev)}
                        >
                            <FormattedMessage {...messages.machinesFilter} />
                            {showMachineFilter ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </Button>
                        <Button
                            sx={{ color: colors.primary[200], ml: 1 }}
                            onClick={() => setShowStopFilter((prev) => !prev)}
                        >
                            <FormattedMessage {...messages.stopFilter} />
                            {showStopFilter ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </Button>
                    </Box>
                </Grid>
                <Grid item xs={12} lg={6} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                    {datepicker}
                    {radiobuttons}
                </Grid>
            </Grid>

            {showMachineFilter && (
                <Grid container sx={{ padding: "5px 20px" }}>
                    <Grid item xs={12}>
                        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                            {machinesOptions.map((machine) => (
                                <FormControlLabel
                                    key={machine.machineName}
                                    control={
                                        <Checkbox
                                            sx={{
                                                color: "secondary",
                                                '&.Mui-checked': {
                                                    color: colors.dashbordGreen[500],
                                                },
                                            }}
                                            checked={selectedMachines.includes(machine.machineName)}
                                            onChange={() => handleMachineChange(machine.machineName)}
                                        />
                                    }
                                    label={machine.machineName}
                                />
                            ))}
                            <FormControlLabel
                                key="all"
                                control={
                                    <Checkbox
                                        sx={{
                                            color: "secondary",
                                            '&.Mui-checked': {
                                                color: colors.dashbordGreen[500],
                                            },
                                        }}
                                        checked={showAllMachines}
                                        onChange={() => handleShowHideAllMachineChange(!showAllMachines)}
                                    />
                                }
                                label={<FormattedMessage {...messages.allMachines} />}
                            />
                        </Box>
                    </Grid>
                </Grid>
            )}

            {showStopFilter && (
                <Grid container sx={{ padding: "5px 20px" }}>
                    <Grid item xs={12}>
                        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                            <FormControlLabel
                                key="undefined_stops"
                                control={
                                    <Checkbox
                                        sx={{
                                            color: "secondary",
                                            '&.Mui-checked': {
                                                color: colors.dashbordGreen[500],
                                            },
                                        }}
                                        checked={showOnlyUndefinedStops}
                                        onChange={() => handleShowOnlyUndefinedStopsChange(showOnlyUndefinedStops)}
                                    />
                                }
                                label={<FormattedMessage {...messages.undefinedStops} />}
                            />
                        </Box>
                    </Grid>
                </Grid>
            )}

            <Box sx={{ ml: "20px", mr: "20px", mb: "20px", mt: "10px" }}>
                <MachineStopGrid
                    fullPage={true}
                    stopReasonCodes={stopReasonCodesQuery.data}
                    machinestops={filteredData}
                    handleRefresh={handleStopRefresh}
                />
            </Box>
        </Box>
    );
};

export default StopsIndex;
